import { type MouseEvent, useCallback, useEffect } from 'react';

import LogoPlus from '@assets/logo-plus.png';
import { Button } from '@components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { useAuth } from '@contexts/auth';
import type { ITreatmentListItem } from '@domain/Treatment';
import { EMajorAreaThemeOrder } from '@enums/MajorAreaThemeOrder';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import type { IListMedicalSpecialtiesOutputDTO } from '@services/medicalSpecialties/dtos/list/IOutputData';
import { medicalSpecialtyService } from '@services/medicalSpecialties/MedicalSpecialtyService';
import type { IAddTreatmentToFavoriteInputDTO } from '@services/treatments/dtos/addToFavorites/IInputData';
import type { IListTreatmentsOutputDTO } from '@services/treatments/dtos/list/IOutputData';
import type { IRemoveTreatmentToFavoriteInputDTO } from '@services/treatments/dtos/removeToFavorites/IInputData';
import { treatmentService } from '@services/treatments/TreatmentService';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Check, ChevronsUpDown, Heart, LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { z } from 'zod';

const formSchema = z.object({
  sort: z.enum(EMajorAreaThemeOrder),
  specialty: z.string(),
});

const sort = [
  {
    value: EMajorAreaThemeOrder.Default,
    label: 'Padrão',
  },
  {
    value: EMajorAreaThemeOrder.Alphabetical,
    label: 'Alfabética',
  },
];

function ListItem({
  isPendingAdd,
  isPendingRemove,
  item,
  onFavorite,
  variablesAdd,
  variablesRemove,
}: {
  isPendingAdd: boolean;
  isPendingRemove: boolean;
  item: ITreatmentListItem;
  onFavorite(input: { id: string; isFavorite: boolean }): Promise<void>;
  variablesAdd?: IAddTreatmentToFavoriteInputDTO;
  variablesRemove?: IRemoveTreatmentToFavoriteInputDTO;
}) {
  function handleToggleFavorite(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    onFavorite({
      id: item.id,
      isFavorite: item.isFavorite,
    });
  }
  if (
    (isPendingAdd && variablesAdd?.id === item.id) ||
    (isPendingRemove && variablesRemove?.id === item.id)
  ) {
    return <LoaderCircle className="text-tertiary size-8 animate-spin" />;
  }
  return (
    <button
      className={cn(
        '[&>svg]:text-tertiary hover:[&>svg]:fill-tertiary/50 cursor-pointer transition-all [&>svg]:size-8',
        item.isFavorite && '[&>svg]:fill-tertiary',
      )}
      onClick={handleToggleFavorite}>
      <Heart />
    </button>
  );
}

function ThemesList() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    isPending: isPendingAdd,
    mutateAsync: mutateAsyncAdd,
    variables: variablesAdd,
  } = useMutation<void, unknown, IAddTreatmentToFavoriteInputDTO>({
    mutationFn: treatmentService.addToFavorites,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['themes'] }),
  });

  const {
    isPending: isPendingRemove,
    mutateAsync: mutateAsyncRemove,
    variables: variablesRemove,
  } = useMutation<void, unknown, IRemoveTreatmentToFavoriteInputDTO>({
    mutationFn: treatmentService.removeToFavorites,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['themes'] }),
  });

  const handleFavorite = useCallback(
    async (input: { id: string; isFavorite: boolean }) => {
      if (input.isFavorite) {
        await mutateAsyncRemove({ id: input.id });
      } else {
        await mutateAsyncAdd({
          id: input.id,
        });
      }
    },
    [mutateAsyncAdd, mutateAsyncRemove],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      sort:
        (searchParams.get('sort') as EMajorAreaThemeOrder) ||
        EMajorAreaThemeOrder.Default,
      specialty: searchParams.get('specialty') || '0',
    },
  });

  const { data: themes } = useSuspenseQuery<IListTreatmentsOutputDTO>({
    queryFn: async () => {
      const medicalSpecialtyId = searchParams.get('specialty');
      const sort = searchParams.get('sort');
      let sortParsed: string | undefined = undefined;
      if (sort === EMajorAreaThemeOrder.Default) {
        sortParsed = 'sortOrder';
      }
      if (sort === EMajorAreaThemeOrder.Alphabetical) {
        sortParsed = 'title';
      }
      const response = await treatmentService.list({
        treatmentCategoryId: params.themeId,
        medicalSpecialtyId:
          !medicalSpecialtyId || medicalSpecialtyId === '0'
            ? undefined
            : medicalSpecialtyId,
        sort: sortParsed,
      });
      return response;
    },
    queryKey: [
      'themes',
      params.themeId,
      searchParams.get('sort'),
      searchParams.get('specialty'),
    ],
    refetchOnWindowFocus: false,
  });

  const { data: medicalSpecialty } =
    useSuspenseQuery<IListMedicalSpecialtiesOutputDTO>({
      queryFn: async () => {
        const response = await medicalSpecialtyService.list({
          treatmentCategoryId: params.themeId,
        });
        return response;
      },
      queryKey: ['medicalSpecialty', params.themeId],
      refetchOnWindowFocus: false,
    });

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    navigate(`${location.pathname}?${searchParams}`, {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'sort' && value.sort) {
        setSearchParams(
          (prev) => {
            prev.set('sort', value.sort!);
            return prev;
          },
          { replace: true },
        );
      }
      if (name === 'specialty' && value.specialty) {
        setSearchParams(
          (prev) => {
            prev.set('specialty', value.specialty!);
            return prev;
          },
          { replace: true },
        );
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Form {...form}>
        <form
          className="grid grid-cols-1 md:grid-cols-2"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:col-start-2">
            <FormField
              control={form.control}
              name="sort"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-tertiary">Ordenação</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {sort.map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="specialty"
              render={({ field }) => (
                <FormItem className="flex w-full max-w-96 flex-col sm:max-w-80">
                  <FormLabel className="text-tertiary">Especialidade</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild className="w-full">
                      <FormControl>
                        <Button
                          className={cn(
                            'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                            !field.value && 'text-muted-foreground',
                          )}
                          role="combobox"
                          variant="outline">
                          {field.value
                            ? [{ name: 'Todas', id: '0' }]
                                .concat(
                                  medicalSpecialty.list.map((item) => ({
                                    name: item.title,
                                    id: item.id,
                                  })),
                                )
                                .find(
                                  (option) =>
                                    option.id.toString() === field.value,
                                )?.name
                            : ''}
                          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput
                          className="placeholder:text-foreground/70 h-9"
                          placeholder="Buscar..."
                        />
                        <CommandList>
                          <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                          <CommandGroup>
                            {[{ name: 'Todas', id: '0' }]
                              .concat(
                                medicalSpecialty.list.map((item) => ({
                                  id: item.id,
                                  name: item.title,
                                })),
                              )
                              .map((option) => (
                                <CommandItem
                                  className="data-[selected=true]:bg-tertiary"
                                  key={option.id}
                                  onSelect={() => {
                                    form.setValue(
                                      `specialty`,
                                      option.id.toString(),
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  value={option.name}>
                                  <PopoverClose className="flex w-full flex-row items-center">
                                    <Check
                                      className={cn(
                                        'mr-2 size-4',
                                        option.id.toString() === field.value
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                    {option.name}
                                  </PopoverClose>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        {params.themeId === '019846fe-b188-76d8-bb29-f3aaac842111' && (
          <>
            <Link
              className={cn(
                'border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex flex-row items-center justify-between gap-2 rounded-lg border-2 p-3 text-base font-bold shadow-md transition-all duration-300 hover:scale-[1.02] lg:p-4 lg:text-lg',
                !user?.accessGranted && 'bg-border',
              )}
              to={
                user?.accessGranted
                  ? '/calculadoras/dose_emergencia'
                  : '/pagamento'
              }>
              Dose emergência pediatria
              {!user?.accessGranted && (
                <div className="bg-primary rounded-md p-1">
                  <img alt="Logo plus" className="w-20" src={LogoPlus} />
                </div>
              )}
            </Link>

            <Link
              className="border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex flex-row items-center justify-between gap-2 rounded-lg border-2 p-3 text-base font-bold shadow-md transition-all duration-300 hover:scale-[1.02] lg:p-4 lg:text-lg"
              to="/calculadoras/dose_sistomatico">
              Dose sintomáticos pediatria
            </Link>

            <Link
              className={cn(
                'border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex flex-row items-center justify-between gap-2 rounded-lg border-2 p-3 text-base font-bold shadow-md transition-all duration-300 hover:scale-[1.02] lg:p-4 lg:text-lg',
                !user?.accessGranted && 'bg-border',
              )}
              to={
                user?.accessGranted
                  ? '/calculadoras/dose_antibiotico'
                  : '/pagamento'
              }>
              Dose antibióticos pediatria
              {!user?.accessGranted && (
                <div className="bg-primary rounded-md p-1">
                  <img alt="Logo plus" className="w-20" src={LogoPlus} />
                </div>
              )}
            </Link>

            <Link
              className={cn(
                'border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex flex-row items-center justify-between gap-2 rounded-lg border-2 p-3 text-base font-bold shadow-md transition-all duration-300 hover:scale-[1.02] lg:p-4 lg:text-lg',
                !user?.accessGranted && 'bg-border',
              )}
              to={
                user?.accessGranted
                  ? '/calculadoras/hidratacao_pediatria'
                  : '/pagamento'
              }>
              Hidratação pediatria (Holliday)
              {!user?.accessGranted && (
                <div className="bg-primary rounded-md p-1">
                  <img alt="Logo plus" className="w-20" src={LogoPlus} />
                </div>
              )}
            </Link>
          </>
        )}

        {themes.list.map((item) => {
          let link = `/temas/${params.themeId}/topicos/${item.id}?name=${searchParams.get('name')}`;
          if (!user?.accessGranted && !item.isAvailable) {
            link = '/pagamento';
          }
          return (
            <Link
              className={cn(
                'border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex flex-row items-center justify-between gap-2 rounded-lg border-2 p-3 text-base font-bold shadow-md transition-all duration-300 hover:scale-[1.02] lg:p-4 lg:text-lg',
                !item.isAvailable && !user?.accessGranted && 'bg-border',
              )}
              key={item.id}
              to={
                link

                // !item.isAvailable && !user?.accessGranted
                //   ? '/pagamento'
                //   : item.id
                //     ? `/calculadoras/${item.id}`
                //     : `/temas/${params.themeId}/topicos/${item.id}?name=${searchParams.get('name')}`
              }>
              {item.title}
              {!item.isAvailable && !user?.accessGranted ? (
                <div className="bg-primary rounded-md p-1">
                  <img alt="Logo plus" className="w-20" src={LogoPlus} />
                </div>
              ) : (
                <div>
                  {item.id !== '99999999999999' &&
                    item.id !== '99999999999998' &&
                    item.id !== '99999999999997' &&
                    item.id !== '99999999999996' && (
                      <ListItem
                        isPendingAdd={isPendingAdd}
                        isPendingRemove={isPendingRemove}
                        item={item}
                        onFavorite={handleFavorite}
                        variablesAdd={variablesAdd}
                        variablesRemove={variablesRemove}
                      />
                    )}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default ThemesList;
